import { useGetActionById } from "@api";
import type { ObjectId } from "@bbj-types/utils";
import CloseIcon from "@mui/icons-material/Close";
import { DialogContent, DialogTitle, IconButton, Stack, Typography } from "@mui/material";
import { Dialog } from "@surfaces/Dialog";
import { Loading } from "@surfaces/Loading";
import dompurify from "isomorphic-dompurify";
import { useEffect, useMemo } from "react";

interface IActionCommentDialogProps {
  actionId: ObjectId;
  isEmail: boolean;
  onClose: () => void;
}

/**
 * Lists ship comments and emails without their inline images (the API replaces them by a link):
 * the full content is reloaded from the single action route.
 */
export const ActionCommentDialog = ({ actionId, isEmail, onClose }: IActionCommentDialogProps) => {
  const [{ data: action, loading, error }, getAction] = useGetActionById();

  useEffect(() => {
    void getAction(actionId);
  }, [actionId, getAction]);

  const content = isEmail ? action?.communication?.email : action?.comments;
  const sanitizedContent = useMemo(() => dompurify.sanitize(content ?? ""), [content]);

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="tablet">
      <DialogTitle>
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h4">{isEmail ? "Email" : "Commentaire"}</Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon color="primary" />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Loading loading={loading}>
          {error ? (
            <Typography color="error">
              {isEmail ? "L'email" : "Le commentaire"} n'a pas pu être chargé. Veuillez réessayer.
            </Typography>
          ) : (
            <Typography
              data-hj-suppress="true"
              variant="body2"
              sx={{ "& img": { maxWidth: "100%", height: "auto" } }}
              // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized HTML content
              dangerouslySetInnerHTML={{ __html: sanitizedContent }}
            />
          )}
        </Loading>
      </DialogContent>
    </Dialog>
  );
};
